export function round(value: number, decimals: number): number {
    // @ts-ignore
    return Number(Math.round(value + 'e' + decimals) + 'e-' + decimals);
}

/**
 * Given a raw score, min score and max score, produce a value [-1,1], in string format.
 * Invalid results should be the empty string.
 * Precondition: minScore <= rawScore <= maxScore if minScore / maxScore / rawScore all exist and are non-empty strings.
 * @param rawScore
 * @param minScore
 * @param maxScore
 * @param mode
 *   "same_sign": Preserve the sign.
 *     If rawScore is negative, the range will be limited to [-1,0].
 *     If rawScore is positive, the range will be limited to [0,1].
 *     If rawScore is zero, the normalizedScore is 0.
 *     Examples:
 *       raw = 50, min = 0, max = 100, normalized = 0.5
 *       raw = -50, min = -100, max = 0, normalized = -0.5
 *       raw = 4, min = -10, max = 10, normalized = 0.4
 *       raw = -4, min = -10, max = 10, normalized = -0.4
 *       raw = -2, min = -5, max = 30, normalized = -0.4
 *       raw = 2, min = -30, max = 5, normalized = 0.4
 *
 */
export function scormRawScoreNormalizer(rawScore: string, minScore: string, maxScore: string, mode: string): string {
    if (rawScore !== "") {
        if (minScore !== "" && maxScore !== "") {
            let raw = parseFloat(rawScore);
            let min = parseFloat(minScore);
            let max = parseFloat(maxScore);
            if (max - min < 0) {
                return "";
            }
            if (mode == "same_sign") {
                return scormRawScoreNormalizerSameSign(raw, min, max);
            }
            else if (mode == "full_avg") {
                return scormRawScoreNormalizerFullAverage(raw, min, max);
            }
            else {
                throw new Error("Unknown raw score normalizer mode: " + mode);
            }
        }
    }
    return "";
}

function scormRawScoreNormalizerSameSign(raw: number, min: number, max: number): string {
    if (max - min == 0) {
        return "0";
    }
    if (raw < 0) {
        if (max > 0) {
            max = 0;
        }
        return "" + round(-(raw - min) / (max - min), 3);
    }
    else if (raw > 0) {
        if (min < 0) {
            min = 0;
        }
        return "" + round((raw - min) / (max - min), 3);
    }
    return "0";
}

function scormRawScoreNormalizerFullAverage(raw: number, min: number, max: number): string {
    if (max - min == 0) {
        return "0";
    }
    return "" + round(((raw - min) / (max - min)) * 2 - 1, 3);
}

export function scormNormalizedScoreToRaw(normalizedScore: string, minScore: string, maxScore: string, mode: string): string {
    if (normalizedScore !== "") {
        if (minScore !== "" && maxScore !== "") {
            let normal = parseFloat(normalizedScore);
            let min = parseFloat(minScore);
            let max = parseFloat(maxScore);
            if (max - min < 0) {
                return "";
            }
            if (mode == "same_sign") {
                return scormNormalizedScoreToRawSameSign(normal, min, max);
            }
            else if (mode == "full_avg") {
                return scormNormalizedScoreToRawFullAverage(normal, min, max);
            }
            else {
                throw new Error("Unknown raw score normalizer mode: " + mode);
            }
        }
    }
    return "";
}

function scormNormalizedScoreToRawSameSign(normal: number, min: number, max: number): string {
    if (normal < 0) {
        if (max > 0) {
            max = 0;
        }
        return "" + round(-normal * (max - min) + min, 3);
    }
    else if (normal > 0) {
        if (min < 0) {
            min = 0;
        }
        return "" + round(normal * (max - min) + min, 3);
    }
    else {
        if (min > 0) {
            return "" + min;
        }
        if (max < 0) {
            return "" + max;
        }
    }
    return "0";
}

function scormNormalizedScoreToRawFullAverage(normal: number, min: number, max: number): string {
    return "" + round((max - min) * ((normal + 1) / 2) + min, 3);
}